import React from 'react';
import { cn } from '@/lib/utils';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

export default function Button({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  iconPosition = 'left',
  className = '',
  disabled,
  type = 'button', // По умолчанию type="button" для предотвращения случайных submit
  ...props
}: ButtonProps) {
  // Важно: курсор pointer на всех кнопках (на десктопе), disabled оставляем not-allowed.
  // Комментарий на русском: добавляем "нажатие" (active) — на iOS нет hover, поэтому без этого кнопки "не ощущаются".
  const baseClasses = 'inline-flex items-center justify-center font-medium cursor-pointer transition-all duration-200 rounded-lg focus-ring disabled:opacity-50 disabled:cursor-not-allowed select-none active:opacity-90 active:scale-[0.98] active:translate-y-[1px]';
  
  const variantClasses = {
    primary: 'bg-site-action text-site-on-accent shadow-sm hover:brightness-95',
    secondary: 'bg-site-raised/50 hover:bg-site-raised text-site-text shadow-sm backdrop-blur-sm border border-site-border-control',
    outline: 'border-2 border-site-accent/50 text-site-accent hover:bg-site-accent/20 hover:border-site-accent backdrop-blur-sm',
    ghost: 'text-site-secondary hover:text-site-text hover:bg-site-surface/50 backdrop-blur-sm',
    danger: 'bg-site-danger hover:bg-site-danger text-site-on-accent shadow-md hover:shadow-lg hover:shadow-red-500/25'
  };
  
  const sizeClasses = {
    xs: 'px-2 py-1 text-xs gap-1',
    sm: 'px-3 py-1.5 text-sm gap-1.5',
    md: 'px-4 py-2 text-base gap-2',
    lg: 'px-6 py-3 text-lg gap-2.5'
  };
  
  const iconSizeClasses = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };
  
  return (
    <button
      type={type}
      className={cn(baseClasses, variantClasses[variant], sizeClasses[size], className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <svg className={cn("animate-spin", iconSizeClasses[size])} fill="none" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" className="opacity-25" />
          <path
            fill="currentColor"
            d="M4 12a8 8 0 0 1 8-8V0a12 12 0 0 0-12 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            className="opacity-75"
          />
        </svg>
      )}
      
      {!loading && icon && iconPosition === 'left' && (
        <span className={iconSizeClasses[size]}>{icon}</span>
      )}
      
      {children}
      
      {!loading && icon && iconPosition === 'right' && (
        <span className={iconSizeClasses[size]}>{icon}</span>
      )}
    </button>
  );
}
